import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, FileTextIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import type { Provider, UpdateVerwijsbriefInput, Verwijsbrief } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import {
  EMPTY_PROVIDER_FORM,
  formToPayload,
  ProviderFormDialog,
} from '../components/ProviderFormDialog.js';
import { apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { CopyFieldButton } from '../components/CopyFieldButton.js';
import {
  REDEN,
  VOORGESCHIEDENIS,
  briefOnderdelen,
  briefTekst,
  maakVerwijsbrief,
} from '../lib/verwijsbrief.js';

/**
 * Verwijsbrieven (ADR 0017). Overzicht van eigen concepten (72 uur houdbaar)
 * en de editor: één brief met per onderdeel een kopieerknop voor het HIS
 * (kopiëren i.p.v. Word sinds 12-10-2026).
 */

function urenOver(expiresAt: string): number {
  return Math.max(0, Math.round((Date.parse(expiresAt) - Date.now()) / 3600_000));
}

const BRON_LABEL: Record<Verwijsbrief['bron'], string> = {
  sara: 'Uit Sara',
  mila: 'Uit Mila',
  handmatig: 'Handmatig',
};

export function VerwijsbrievenPage(): JSX.Element {
  const { id } = useParams();
  return <Layout>{id ? <Editor id={id} /> : <Overzicht />}</Layout>;
}

function Overzicht(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<Verwijsbrief[] | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ items: Verwijsbrief[] }>('/api/v1/verwijsbrieven')
      .then((r) => setItems(r.items))
      .catch(() => setFout('De verwijsbrieven konden niet worden geladen.'));
  }, []);

  async function nieuw(): Promise<void> {
    const b = await maakVerwijsbrief({ bron: 'handmatig' }, user?.displayName ?? '');
    navigate(`/verwijsbrieven/${b.id}`);
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 md:px-8">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-ink-900">Verwijsbrieven</h1>
        <button
          type="button"
          onClick={() => void nieuw()}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          <PlusIcon size={16} /> Nieuwe verwijsbrief
        </button>
      </div>
      <p className="mb-6 text-sm text-ink-900/70">
        Concepten verdwijnen automatisch 72 uur na het aanmaken. Je kopieert de tekst naar de brief
        in het HIS of ZorgDomein; patiëntgegevens staan daar al, nooit hier.
      </p>
      {fout && <p className="text-sm text-error">{fout}</p>}
      {items === null && !fout && <p className="text-sm text-ink-900/60">Bezig met laden…</p>}
      {items?.length === 0 && (
        <p className="rounded-xl border border-dashed border-ink-100 p-6 text-center text-sm text-ink-900/60">
          Geen concepten. Maak er een aan via Sara, via Mila (Verwijzen) of met de knop hierboven.
        </p>
      )}
      <ul className="space-y-2">
        {items?.map((b) => (
          <li key={b.id}>
            <Link
              to={`/verwijsbrieven/${b.id}`}
              className="flex items-center gap-4 rounded-xl border border-ink-100 bg-white p-4 hover:border-brand-300"
            >
              <FileTextIcon size={20} className="shrink-0 text-brand-600" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink-900">
                  {b.geadresseerde?.naam ?? 'Nog geen geadresseerde'}
                </span>
                <span className="block text-xs text-ink-900/60">
                  {BRON_LABEL[b.bron]} · aangemaakt{' '}
                  {new Date(b.createdAt).toLocaleString('nl-NL', {
                    timeZone: 'Europe/Amsterdam',
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-mosterdgeel-10 px-2.5 py-1 text-xs text-ring1-fg">
                nog {urenOver(b.expiresAt)} uur
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Editor ──────────────────────────────────────────────────────────────────

function adresVan(p: Provider): string {
  return [p.location.address, p.location.city].filter(Boolean).join(', ');
}

function AanbiederZoeker({ onKies }: { onKies: (p: Provider) => void }): JSX.Element {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Provider[]>([]);
  const [toevoegen, setToevoegen] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      apiGet<{ items: Provider[] }>('/api/v1/providers', { q: q.trim(), limit: '8' })
        .then((r) => setHits(r.items))
        .catch(() => setHits([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <div className="relative">
        <SearchIcon size={15} className="absolute left-3 top-2.5 text-ink-900/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Zoek in de Sociale Kaart"
          className="w-full rounded-lg border border-ink-100 py-2 pl-9 pr-3 text-sm"
        />
      </div>
      {hits.length > 0 && (
        <ul className="mt-1 max-h-60 overflow-auto rounded-lg border border-ink-100 bg-white">
          {hits.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => {
                  onKies(p);
                  setQ('');
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-brand-50"
              >
                <span className="font-medium text-ink-900">{p.name}</span>
                <span className="block text-xs text-ink-900/60">{adresVan(p)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => setToevoegen(true)}
        className="mt-2 text-xs font-medium text-petrol-500 hover:underline"
      >
        Staat de praktijk er niet tussen? Voeg hem toe
      </button>
      {toevoegen && (
        <ProviderFormDialog
          title="Praktijk voorstellen"
          initial={EMPTY_PROVIDER_FORM}
          mode="create"
          variant="suggestion"
          onClose={() => setToevoegen(false)}
          onSubmit={async (form) => {
            // Zelfde voorstelroute als in de Sociale Kaart: de praktijk komt
            // direct in de brief, en is voor anderen pas zichtbaar na
            // goedkeuring door een beheerder (besluit Jos 30-9-2026).
            const {
              status: _s,
              source: _src,
              ...rest
            } = formToPayload(form) as Record<string, unknown>;
            void _s;
            void _src;
            const p = await apiPost<Provider>('/api/v1/providers/suggestions', rest);
            onKies(p);
            setToevoegen(false);
          }}
        />
      )}
    </div>
  );
}

function Editor({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  const [b, setB] = useState<Verwijsbrief | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [opslaan, setOpslaan] = useState<'bewaard' | 'bezig' | 'fout'>('bewaard');
  const wachtrij = useRef<UpdateVerwijsbriefInput>({});
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const keten = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    apiGet<Verwijsbrief>(`/api/v1/verwijsbrieven/${id}`)
      .then(setB)
      .catch(() =>
        setFout('Deze verwijsbrief bestaat niet (meer). Concepten verdwijnen na 72 uur.'),
      );
  }, [id]);

  // Opslaan na 800 ms stilte; alle wijzigingen in dat venster in één PATCH.
  // PATCHes lopen na elkaar, anders kan een trage oudere de nieuwere tekst
  // op de server overschrijven.
  function flush(): Promise<unknown> {
    clearTimeout(timer.current);
    const patch = wachtrij.current;
    wachtrij.current = {};
    if (Object.keys(patch).length === 0) return keten.current;
    keten.current = keten.current
      .then(() => apiPatch<Verwijsbrief>(`/api/v1/verwijsbrieven/${id}`, patch))
      .then(() => setOpslaan('bewaard'))
      .catch(() => setOpslaan('fout'));
    return keten.current;
  }

  function set(p: UpdateVerwijsbriefInput): void {
    setB((prev) => (prev ? { ...prev, ...p } : prev));
    wachtrij.current = { ...wachtrij.current, ...p };
    setOpslaan('bezig');
    // De leesbevestiging meteen opslaan: de server weigert de kopieer-auditregel
    // zolang `nagelezen` daar nog false is.
    if ('nagelezen' in p) void flush();
    else {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), 800);
    }
  }

  if (fout) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10 text-center">
        <p className="mb-4 text-sm text-ink-900/70">{fout}</p>
        <Link to="/verwijsbrieven" className="text-sm font-medium text-petrol-500 hover:underline">
          Naar het overzicht
        </Link>
      </div>
    );
  }
  if (!b) return <p className="p-8 text-sm text-ink-900/60">Bezig met laden…</p>;

  const huidig = b;
  const gekopieerd = (): void => {
    void flush()
      .then(() => apiPost(`/api/v1/verwijsbrieven/${id}/gekopieerd`))
      .catch(() => {});
  };
  const tekstveld = (k: keyof Verwijsbrief['inhoud'], titel: string) => (
    <label className="block">
      <span className="text-xs font-semibold text-ink-900/70">{titel}</span>
      <textarea
        value={huidig.inhoud[k]}
        onChange={(e) => set({ inhoud: { ...huidig.inhoud, [k]: e.target.value } })}
        rows={k === 'klachten' ? 5 : 3}
        placeholder="Nog leeg"
        className="mt-1 w-full resize-y rounded-lg border border-ink-100 bg-white p-2 text-[13px] leading-relaxed focus:border-brand-400 focus:outline-none"
      />
    </label>
  );
  // AI Act art. 50 + besluit Jos 12-10-2026: tekst van Mila is pas te kopiëren
  // nadat de POH bevestigt dat ze hem gelezen heeft.
  const aiOngelezen = b.uitTranscript && !b.nagelezen;
  const aiLabel = aiOngelezen && (
    <span className="mb-1 inline-block rounded bg-mosterdgeel-10 px-1.5 py-0.5 text-[11px] font-medium text-ring1-fg">
      Concept van Mila (AI), nog niet nagelezen
    </span>
  );
  const onderdelen = briefOnderdelen(b);
  const tekstVan = (titel: string): string =>
    onderdelen.find((o) => o.titel === titel)?.tekst ?? '';

  async function verwijder(): Promise<void> {
    clearTimeout(timer.current);
    await apiDelete(`/api/v1/verwijsbrieven/${id}`);
    navigate('/verwijsbrieven');
  }

  // Eén brief om in te vullen (feedback Jos 12-10-2026): de keuzes staan op hun
  // eigen plek in de brief, geen apart formulier ernaast.
  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <Link
            to="/verwijsbrieven"
            className="inline-flex items-center gap-1 text-sm text-petrol-500 hover:underline"
          >
            <ArrowLeftIcon size={14} /> Alle verwijsbrieven
          </Link>
          <span className="text-xs text-ink-900/60">
            {opslaan === 'bezig'
              ? 'Opslaan…'
              : opslaan === 'fout'
                ? 'Opslaan mislukt'
                : 'Opgeslagen'}{' '}
            · verdwijnt over {urenOver(b.expiresAt)} uur
          </span>
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink-900">Verwijsbrief</h1>
            <p className="text-sm text-ink-900/70">
              Vul de brief in en kopieer hem naar het HIS of ZorgDomein.
            </p>
          </div>
          {!aiOngelezen ? (
            <CopyFieldButton
              text={briefTekst(b)}
              label="Kopieer hele brief"
              title="Kopieer alle onderdelen in één keer"
              onCopy={gekopieerd}
            />
          ) : (
            <span className="text-xs text-error">Bevestig eerst dat je de tekst hebt gelezen</span>
          )}
        </div>

        <article className="space-y-5 rounded-sm bg-white p-6 text-[13px] leading-relaxed text-ink-900 shadow-lg ring-1 ring-ink-100 md:p-8">
          {b.uitTranscript && (
            <label className="flex items-start gap-2 rounded-md border border-mosterdgeel-400 bg-mosterdgeel-10 px-3 py-2">
              <input
                type="checkbox"
                checked={b.nagelezen}
                onChange={(e) => set({ nagelezen: e.target.checked })}
                className="mt-0.5"
              />
              <span>
                Klachten, hulpvraag en eerdere stappen zijn een samenvatting door Mila (AI) van het
                gesprek. <b>Ik heb de tekst gelezen en ben verantwoordelijk voor de inhoud.</b>
              </span>
            </label>
          )}
          <p className="text-[12px] text-ink-900/60">
            Deze vakken vul je zelf in, niet Mila: vermoeden DSM-5, procedurevoorstel,
            heraanmelding, suïcidaliteit en besproken met patiënt. Journaal en medicatie komen uit
            het HIS. Na het plakken: voorletters en placeholders zoals [DATUM_1] vervangen en de
            tekst nalezen op herleidbare details.
          </p>

          <Onderdeel
            titel="Aan"
            tekst={tekstVan('Aan')}
            onCopy={gekopieerd}
            geblokkeerd={aiOngelezen}
          >
            {b.geadresseerde ? (
              <div className="flex items-start justify-between gap-2">
                <span>
                  {b.geadresseerde.naam}
                  <br />
                  {b.geadresseerde.adres}
                </span>
                <button
                  type="button"
                  onClick={() => set({ geadresseerde: null })}
                  className="text-xs text-petrol-500 hover:underline"
                >
                  Wijzig
                </button>
              </div>
            ) : (
              <>
                <AanbiederZoeker
                  onKies={(p) =>
                    set({ geadresseerde: { providerId: p.id, naam: p.name, adres: adresVan(p) } })
                  }
                />
                <p className="mt-1 text-xs text-ink-900/60">
                  Leeg laten mag: dan kies je de ontvanger in het HIS of in ZorgDomein.
                </p>
              </>
            )}
          </Onderdeel>

          <Onderdeel
            titel={REDEN}
            tekst={tekstVan(REDEN)}
            onCopy={gekopieerd}
            geblokkeerd={aiOngelezen}
          >
            {aiLabel}
            <div className="space-y-2">
              {tekstveld('klachten', 'Klachten')}
              {tekstveld('hulpvraag', 'Hulpvraag')}
            </div>
          </Onderdeel>

          <Onderdeel
            titel={VOORGESCHIEDENIS}
            tekst={tekstVan(VOORGESCHIEDENIS)}
            onCopy={gekopieerd}
            geblokkeerd={aiOngelezen}
          >
            {aiLabel}
            {tekstveld('eerdereStappen', 'Eerdere hulpverlening uit het gesprek')}
            <p className="mt-1 text-xs text-ink-900/60">
              Mila noemt geen diagnoses. Eerdere diagnoses vul je zelf aan uit het HIS.
            </p>
          </Onderdeel>

          <Onderdeel
            titel="Afsluiting"
            tekst={tekstVan('Afsluiting')}
            onCopy={gekopieerd}
            geblokkeerd={aiOngelezen}
          >
            Met vriendelijke groet,
            <label className="mt-2 flex flex-wrap items-center gap-2">
              Opgesteld door:
              <input
                value={b.opstellerNaam}
                onChange={(e) => set({ opstellerNaam: e.target.value })}
                className="min-w-0 flex-1 rounded-lg border border-ink-100 px-2 py-1 text-[13px]"
              />
              , POH-GGZ
            </label>
          </Onderdeel>
        </article>

        <button
          type="button"
          onClick={() => void verwijder()}
          className="mt-4 inline-flex items-center gap-1.5 text-xs text-ink-900/60 hover:text-error"
        >
          <Trash2Icon size={13} /> Concept nu verwijderen
        </button>
      </div>
    </div>
  );
}

function Onderdeel({
  titel,
  tekst,
  onCopy,
  geblokkeerd,
  children,
}: {
  titel: string;
  tekst: string;
  onCopy: () => void;
  geblokkeerd: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <section>
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-bold text-ink-900">{titel}</h2>
        <CopyFieldButton text={tekst} onCopy={onCopy} disabled={geblokkeerd} />
      </div>
      {children}
    </section>
  );
}
